<script setup lang="ts">
import {
  useDiningsStore, useEventsStore, useFacilitiesStore, useHotelStore,
  useLabelsStore, useMeetingsStore, usePagesStore, useRoomsStore,
  useSettingStore,
} from 'pinia-store'
import { useRoute, useRouter } from 'vue-router'
import { initMenu, linkBookingNow } from '@ui/composables'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

import type { IPagesMenuParams } from 'pinia-store/pages'

/**
 * NOTE:
 * Some composable functions provided by @nuxtjs/i18n such as useI18n are auto-imported by Nuxt.
 * If you want to import them explicitly, you can use #imports as follows:
 */
import { stripHtml } from '@general-function'
import type { StringArraySupportOption } from 'prettier'
import { useI18n, useLocalePath } from '#imports'

const { t, locale: language } = useI18n()
const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const useSetting = useSettingStore()
const usePages = usePagesStore()
const useHotel = useHotelStore()
const useLabels = useLabelsStore()
const useFacilities = useFacilitiesStore()
const useMeetings = useMeetingsStore()
const useRooms = useRoomsStore()
const useDinings = useDiningsStore()
const useEvents = useEventsStore()

const meetings = computed(() => useMeetings.getList)
const events = computed(() => useEvents.getList)
const rooms = computed(() => useRooms.getList)
const facilities = computed(() => useFacilities.getList)
const offers = ref([1])
const press = ref([1])
const dinings = computed(() => useDinings.getList)

const labels = useLabels.getLabels
const profile = useHotel.getProfile
const isAtNotFoundPage = computed(() => route.name === 'not-found')
const menu = computed<IPagesMenuParams[]>(() => initMenu(usePages.getMenu))

const btnLabel = stripHtml(
  labels?.ROOMS_PAGE_BTN_LABEL,
  false,
  t('Book Now'),
)

function handleClickMenu(isShowSidebarMenu = false) {
  useSetting.setShowSidebarMenu(isShowSidebarMenu || !useSetting.showSidebarMenu)
}

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smallerOrEqual('sm')

function createMenuLink(obj) {
  let req = obj
  if (isAtNotFoundPage) {
    req = {
      ...req,
      params: {
        ...req?.params,
        language: route.params?.language || language.value,
      },
    }
  }
  else {
    req = localePath({ ...req })
  }
  return req
}
function checkMenu(slug) {
  let bool = true
  if (slug === 'whats-new' && (!press.value || press.value.length === 0))
    bool = false
  else if (slug === 'offers' && (!offers.value || offers.value.length === 0))
    bool = false
  else if (slug === 'facilities' && (!facilities.value || facilities.value.length === 0))
    bool = false
  else if (slug === 'meetings' && (!meetings.value || meetings.value.length === 0))
    bool = false
  else if (slug === 'events' && (!events.value || events.value.length === 0))
    bool = false
  else if (slug === 'rooms' && (!rooms.value || rooms.value.length === 0))
    bool = false
  else if (slug === 'dinings' && (!dinings.value || dinings.value.length === 0))
    bool = false
  return bool
}
function handleClick(val: {
  slug: string
  child?: StringArraySupportOption
  is_spa?: boolean
}) {
  useSetting.setShowSidebarMenu(false)
  if (!val?.slug)
    return

  const el = document.getElementById(`section-${val.slug}`)
  const name = route.name
  if (!name)
    return
  if (val?.is_spa
  && name !== 'home') {
    return router.push(createMenuLink({
      name: 'home',
      params: {
        language: route.params?.language || language.value,
        slug: val?.child,
      },
      query: {
        page: val.slug,
      },
    }))
  }
  else if (el && val?.is_spa) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
  else if (val?.slug && !val?.is_spa) {
    return router.push(createMenuLink({
      name: val?.slug,
      params: {
        slug: val?.child,
      },
    }))
  }
}
watch(
  route,
  (to) => {
    if (to.name)
      useSetting.setShowSidebarMenu(false)
    if (to?.query?.page && to.name === 'home' && typeof document !== 'undefined') {
      setTimeout(() => {
        const el = document.getElementById(`section-${to.query.page}`)
        if (el)
          el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  },
  { flush: 'pre', immediate: true, deep: true },
)

onBeforeUnmount(() => {
  useSetting.setShowSidebarMenu(false)
})

const scrollPosition = ref<number>(0)

const isHeaderFixed = computed(() => {
  return scrollPosition.value > 1
})

function handleScroll() {
  scrollPosition.value = window.scrollY
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  if (typeof window !== 'undefined')
    window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <header
    :class="{ fixed: isHeaderFixed }"
    class="fixed_header submenu_version reveal_header menu_v4 z-30"
  >
    <div class="layer" />
    <!-- Opacity Mask -->
    <div class="header-wrapper mx-4 my-3 flex p-0 py-3 lg:!py-0">
      <div class="w-full flex items-center">
        <div v-if="profile?.hotel_logo_color" class="w-9/12 pl-0 lg:!w-2/12 md:!w-2/12 xl:!w-2/12 lg:!pl-4">
          <NuxtLink :to="localePath({ name: 'home' })" class="logo_normal">
            <img
              :src="profile?.hotel_logo_color"
              :alt="profile?.hotel_name"
              sizes="278px lg:54px sm:90px"
              class="h-10 w-fit lg:h-14"
              placeholder="[278, 54, 50, 5]"
              preload
            />
          </NuxtLink>
          <NuxtLink :to="localePath({ name: 'home' })" class="logo">
            <img
              :src="profile?.hotel_logo_color"
              :alt="profile?.hotel_name"
              sizes="278px lg:54px sm:90px"
              class="h-10 w-fit lg:h-14"
              placeholder="[278, 54, 50, 5]"
              preload
            />
          </NuxtLink>
        </div>
        <div class="w-3/12 lg:w-10/12">
          <div class="main-menu">
            <div v-if="profile && profile?.hotel_logo_color" class="logo_panel">
              <img
                :src="profile?.hotel_logo"
                :alt="profile?.hotel_name"
                width="135"
                height="45"
                sizes="100%"
              />
            </div>
            <nav v-if="menu && menu?.length" id="mainNav" aria-label="Main">
              <ul>
                <li v-for="(item, index) in menu" :key="index">
                  <a
                    v-if="item.redirect_url && item.title"
                    :class="`${route?.name === item.slug ? 'text-secondary' : 'black'}`"
                    :title="item.title"
                    :href="item.redirect_url"
                    rel="nofollow noopener noreferrer"
                  >
                    {{ stripHtml(item.title, false) }}
                  </a>
                  <NuxtLink
                    v-else-if="item?.slug && checkMenu(item?.slug) && !item.is_spa"
                    :to="localePath({
                      name: item.slug,
                      params: {
                        slug: item?.child,
                      },
                    })"
                    :class="`${route?.name === item.slug ? 'text-secondary' : 'black'}`"
                    :title="item.title"
                  >
                    {{ stripHtml(item.title, false) }}
                  </NuxtLink>
                  <a
                    v-else-if="item?.slug && checkMenu(item?.slug)"
                    :class="`${route?.name === item.slug ? 'text-secondary' : 'black'}`"
                    :title="item.title"
                    @click.prevent="handleClick(item)"
                  >
                    {{ stripHtml(item.title, false) }}
                  </a>
                </li>
                <li v-if="!isMobile && profile?.hotel_flight_url">
                  <NuxtLink
                    :title="t('book_flight')"
                    rel="nofollow"
                    class="booking-btn-flight pba-btn-secondary-outline whitespace-pre"
                    :to="profile?.hotel_flight_url"
                    target="_blank"
                  >
                    {{ t('book_flight') }}
                  </NuxtLink>
                </li>
                <li v-if="!isMobile && profile?.hotel_simplebooking">
                  <NuxtLink
                    title="Book Now"
                    rel="nofollow"
                    class="booking-btn pba-btn-secondary whitespace-pre"
                    @click="
                      (event: MouseEvent) =>
                        linkBookingNow(event, {
                          source: 'general',
                          hotelSimplebooking: profile?.hotel_simplebooking,
                          splitDate: [],
                          adults: 1,
                          childs: 0,
                        })
                    "
                  >
                    {{ btnLabel }}
                  </NuxtLink>
                </li>
              </ul>
            </nav>
          </div>
          <div class="hamburger_2 float-right">
            <button type="button" title="hamburger" class="hamburger__box shadow-none" @click="handleClickMenu()">
              <div class="hamburger__inner" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- container -->
  </header>
  <!-- End Header -->

  <div class="nav_panel" :class="[useSetting.showSidebarMenu ? 'show' : '']">
    <div class="w-full flex items-center justify-between px-4">
      <NuxtLink
        v-if="profile && profile?.hotel_logo_color"
        type="button"
        title="logo"
        :to="localePath({ name: 'home' })"
        class="ml-6 mt-6"
      >
        <img
          :src="profile?.hotel_logo_color"
          :alt="profile?.hotel_name"
          width="100"
          style="filter: saturate(0) contrast(0) brightness(10)"
        />
      </NuxtLink>
      <div class="flex items-center">
        <button
          type="button"
          title="Close"
          class="closebt open_close_nav_panel mr-4 mt-10 shadow-none"
          @click="handleClickMenu()"
        >
          <svg
            class="m-auto h-6 w-6"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Close menu</span>
        </button>
      </div>
    </div>
    <div class="sidebar-navigation">
      <nav v-if="menu && menu?.length" aria-label="Sidebar">
        <ul class="level-1">
          <li v-for="(item, index) in menu" :key="index">
            <a
              v-if="item.redirect_url && item.title"
              :class="`${route?.name === item.slug ? 'text-secondary' : ''}`"
              :title="item.title"
              :href="item.redirect_url"
              rel="nofollow noopener noreferrer"
            >
              {{ stripHtml(item.title, false) }}
            </a>
            <NuxtLink
              v-else-if="item?.slug && checkMenu(item?.slug) && !item.is_spa"
              :to="localePath({
                name: item.slug,
                params: {
                  slug: item?.child,
                },
              })"
              :class="`${route?.name === item.slug ? 'text-secondary' : ''}`"
              :title="item.title"
            >
              {{ stripHtml(item.title, false) }}
            </NuxtLink>
            <a
              v-else-if="item?.slug && checkMenu(item?.slug)"
              :title="item.title"
              :class="`${route?.name === item.slug ? 'text-secondary' : ''}`"
              @click.prevent="handleClick(item)"
            >
              {{ stripHtml(item.title, false) }}
            </a>
          </li>
        </ul>
        <!-- /panel_footer -->
      </nav>
    </div>
    <!-- /sidebar-navigation -->
  </div>
  <!-- /nav_panel -->
</template>

<style lang="scss">
@import './header/component.scss';
</style>
